// src/App.jsx
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";

// === CONTEXT PROVIDERS ===
import { InvoicesProvider } from "./context/InvoicesContext";
import { PurchaseOrdersProvider } from "./context/PurchaseOrdersContext";
import { ExpensesProvider } from "./context/ExpensesContext";
import { BillsProvider } from "./context/BillsContext";

// === PAGES ===
import Landing from "./Pages/LandingPage/Landing";
import Callback from "./Pages/LandingPage/CallBack";

// === DASHBOARD LAYOUT ===
import DashboardLayout from "./Pages/Dashboard/DashboardLayout";

// === ALL DASHBOARD PAGES ===
import Dashboard from "./Pages/Dashboard/Dashboard";
import Customers from "./Pages/Dashboard/Customers";
import CustomerData from "./Pages/Dashboard/Sales/CustomerData";
import Quotes from "./Pages/Dashboard/Quotes";
import Sales_Orders from "./Pages/Dashboard/Sales_Orders";
import Invoices from "./Pages/Dashboard/Invoices";
import AddInvoices from "./Pages/Dashboard/Sales/AddInvoices";
import ViewInvoices from "./Pages/Dashboard/Sales/ViewInvoices";
import Recurring_Invoices from "./Pages/Dashboard/Recurring_Invoices";
import Delivery_Challans from "./Pages/Dashboard/Delivery_Challans";
import AddDelivery_Challans from "./Pages/Dashboard/Sales/AddDelivery_Challans";
import ViewDelivery_Challans from "./Pages/Dashboard/Sales/ViewDelivery_Challans";
import Payments_Received from "./Pages/Dashboard/Payments_Received";
import Credit_Notes from "./Pages/Dashboard/Credit_Notes";
import AddCreditNotes from "./Pages/Dashboard/Sales/AddCreditNotes";
import ViewCreditNotes from "./Pages/Dashboard/Sales/ViewCreditNotes";

// === PURCHASE ===
import Vendors from "./Pages/Purchase/Vendors";
import VendorsData from "./Pages/Purchase/PurchaseData/VendorsData";
import Expenses from "./Pages/Purchase/Expenses";
import AddExpenses from "./Pages/Purchase/PurchaseData/AddExpenses";
import ViewExpenses from "./Pages/Purchase/PurchaseData/ViewExpenses";
import PurchaseOrders from "./Pages/Purchase/PurchaseOrders";
import AddPurchaseOrders from "./Pages/Purchase/PurchaseData/AddPurchaseOrders";
import ViewPurchaseOrders from "./Pages/Purchase/PurchaseData/ViewPurchaseOrders";
import Bills from "./Pages/Purchase/Bills";
import AddBills from "./Pages/Purchase/PurchaseData/AddBills";
import ViewBills from "./Pages/Purchase/PurchaseData/ViewBills";

// === SETTINGS & OTHERS ===
import Organization from "./Pages/Dashboard/Organization";
import Reports from "./Pages/Dashboard/Reports";
import Changelog from "./Pages/Dashboard/Changelog"; // ← NEW IMPORT

// ==================== AUTH SYNC ====================
const useAuthSync = () => {
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL ||
    (process.env.NODE_ENV === "production"
      ? "https://finance.dev.marsforce.in"
      : "http://localhost:8030");
  const navigate = useNavigate();

  useEffect(() => {
    const syncUser = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/me`, {
          credentials: "include",
        });
        const data = await res.json();
        if (data.success) {
          localStorage.setItem("userId", data.user.userId);
          localStorage.setItem("userName", data.user.name);
          localStorage.setItem("userEmail", data.user.email);
        } else {
          localStorage.clear();
          if (window.location.pathname !== "/") {
            navigate("/", { replace: true });
          }
        }
      } catch (err) {
        console.log("Backend not ready yet...");
      }
    };
    syncUser();
    const interval = setInterval(syncUser, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [navigate]);
};

// ==================== PROTECTED ROUTE ====================
const ProtectedRoute = ({ children }) => {
  useAuthSync();
  const token = localStorage.getItem("accessToken");
  const profile = localStorage.getItem("profile");
  if (!token || !profile) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// ==================== LOGOUT ====================
const Logout = () => {
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL ||
    (process.env.NODE_ENV === "production"
      ? "https://finance.dev.marsforce.in"
      : "http://localhost:8030");

  useEffect(() => {
    localStorage.clear();
    sessionStorage.clear();
    fetch(`${backendUrl}/auth/logout`, { credentials: "include" }).finally(
      () => {
        const redirect = encodeURIComponent(window.location.origin);
        window.location.href = `https://login.microsoftonline.com/${process.env.REACT_APP_TENANT_ID}/oauth2/v2.0/logout?post_logout_redirect_uri=${redirect}`;
      }
    );
  }, [backendUrl]);

  return <div>Logging out…</div>;
};

// ==================== MAIN APP ====================
const App = () => (
  <Router>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/auth/microsoft/callback" element={<Callback />} />
      <Route path="/logout" element={<Logout />} />

      {/* Protected Dashboard Routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <InvoicesProvider>
              <PurchaseOrdersProvider>
                <ExpensesProvider>
                  <BillsProvider>
                    <DashboardLayout />
                  </BillsProvider>
                </ExpensesProvider>
              </PurchaseOrdersProvider>
            </InvoicesProvider>
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<Dashboard />} />

        {/* Sales */}
        <Route path="sales/customers" element={<Customers />} />
        <Route path="sales/customers/new" element={<CustomerData />} />
        <Route path="sales/customers/:id" element={<CustomerData />} />
        <Route path="sales/quotes" element={<Quotes />} />
        <Route path="sales/sales-orders" element={<Sales_Orders />} />
        <Route path="sales/invoices" element={<Invoices />} />
        <Route path="sales/invoices/new" element={<AddInvoices />} />
        <Route path="sales/invoices/:id" element={<ViewInvoices />} />
        <Route
          path="sales/recurring-invoices"
          element={<Recurring_Invoices />}
        />
        <Route path="sales/delivery-challans" element={<Delivery_Challans />} />
        <Route
          path="sales/delivery-challans/new"
          element={<AddDelivery_Challans />}
        />
        <Route
          path="sales/delivery-challans/:id"
          element={<ViewDelivery_Challans />}
        />
        <Route path="sales/payments-received" element={<Payments_Received />} />
        <Route path="sales/credit-notes" element={<Credit_Notes />} />
        <Route path="sales/credit-notes/new" element={<AddCreditNotes />} />
        <Route
          path="sales/credit-notes/:creditNoteNumber"
          element={<ViewCreditNotes />}
        />

        {/* Purchase */}
        <Route path="purchase/vendors" element={<Vendors />} />
        <Route path="purchase/vendors/new" element={<VendorsData />} />
        <Route path="purchase/vendors/:id" element={<VendorsData />} />
        <Route path="purchase/expenses" element={<Expenses />} />
        <Route path="purchase/expenses/new" element={<AddExpenses />} />
        <Route path="purchase/expenses/:id" element={<ViewExpenses />} />
        <Route path="purchase/purchase-orders" element={<PurchaseOrders />} />
        <Route
          path="purchase/purchase-orders/new"
          element={<AddPurchaseOrders />}
        />
        <Route
          path="purchase/purchase-orders/view/:id"
          element={<ViewPurchaseOrders />}
        />
        <Route path="purchase/bills" element={<Bills />} />
        <Route path="purchase/bills/new" element={<AddBills />} />
        <Route path="purchase/bills/view/:id" element={<ViewBills />} />

        {/* Settings & Others */}
        <Route path="settings/organization" element={<Organization />} />
        <Route path="reports" element={<Reports />} />

        {/* NEW: Changelog Route */}
        <Route path="changelog" element={<Changelog />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  </Router>
);

export default App;
